import React, { useCallback, useEffect, useMemo } from "react";
// utils
import { ErrorBoundary } from "@swiggy-private/core-ui";
import { isOneLite, isSwiggyBlack } from "@super-ui/components/swiggy-one-widget-dls-wrapper/helpers";
import { isEmpty } from "@swiggy-private/js-utils";
// types
import { M2CHalfcardProps } from "./types";
// components
import { M2CSwiggyOneHalfCard, M2C_HALFCARD_TEST_IDS } from "@super-ui/components/m2c-swiggy-one-halfcard";
import { M2CSwiggyBlackHalfCard } from "@super-ui/components/m2c-swiggy-black-halfcard";
// constants
import { M2C_HALFCARD_GRATIFICATION_FREQUENCY_CAPPING_KEY, m2cCardCommsContainerId } from "../constants";
// hooks
import { useGetM2CHalfcardProps } from "../hooks/useGetM2CHalfcardProps";
import { NEWRELIC_TABLES, recordNREvent, useSessionStorage } from "@swiggy-private/checkout-core";
import { recordSwiggyOneNREventDataTestIdVisibility, SWIGGY_ONE_COMPONENT } from "@super-ui/helpers/SwiggyOneNREvents";

const M2CHalfcard: React.FC<M2CHalfcardProps> = (props) => {
    const {
        cardComms,
        addSuper,
        hideM2CHalfcard,
        m2cContainerId = m2cCardCommsContainerId,
        setShowM2Chalfcard,
        ignoreCapping = false,
        onClose,
        flowType,
    } = props;

    const [isM2CGratificationShown, setIsM2CGratificationShown] = useSessionStorage(
        M2C_HALFCARD_GRATIFICATION_FREQUENCY_CAPPING_KEY,
        false,
    );

    const isOneLiteType = useMemo(() => isOneLite(cardComms?.planDetails), [cardComms?.planDetails]);

    const isSwiggyBlackType = useMemo(() => isSwiggyBlack(cardComms?.planDetails), [cardComms?.planDetails]);

    const { shouldMountM2C, preStateData, postStateData } = useGetM2CHalfcardProps({
        ...cardComms,
        addSuper,
        hideM2CHalfcard,
        updateCartInProgress: props.updateCartInProgress,
        setShowM2Chalfcard,
        ignoreCapping,
        flowType,
    });

    const m2cFormattedProps = useMemo(() => {
        const commonProps = { isOneLite: isOneLiteType, root: m2cContainerId, onClose };
        if (isEmpty(postStateData)) {
            return {
                preStateData,
                ...commonProps,
            };
        }
        return {
            postStateData: { ...postStateData, setIsM2CGratificationShown },
            ...commonProps,
        };
    }, [isOneLiteType, m2cContainerId, onClose, postStateData, preStateData, setIsM2CGratificationShown]);

    const shouldHideM2C = useMemo(() => {
        if (ignoreCapping) {
            return false;
        } else if (
            hideM2CHalfcard ||
            (!shouldMountM2C && isEmpty(postStateData)) ||
            (!isEmpty(postStateData) && isM2CGratificationShown) // if gratification data is not empty and gratification is already shown in that session
        ) {
            return true;
        }
        return false;
    }, [hideM2CHalfcard, ignoreCapping, isM2CGratificationShown, postStateData, shouldMountM2C]);

    useEffect(() => {
        recordSwiggyOneNREventDataTestIdVisibility(
            SWIGGY_ONE_COMPONENT.M2C_HALFCARD,
            M2C_HALFCARD_TEST_IDS.HALFCARD,
            "",
            1800,
        );
        if (shouldHideM2C) {
            let hideReason = "OVERLAY_BLOCKED";
            if (hideM2CHalfcard) hideReason = "MODAL_VISIBLE";
            else if (!isEmpty(postStateData) && isM2CGratificationShown) hideReason = "GRATIFICATION_ALREADY_SHOWN";
            else if (!shouldMountM2C && isEmpty(postStateData)) hideReason = "NOT_MOUNTED";
            recordNREvent({
                eventType: NEWRELIC_TABLES.DEBUG_SWIGGY_ONE_JOURNEY,
                component: "M2C_HALFCARD",
                state: "HIDDEN",
                hideReason,
            });
        } else {
            recordNREvent({
                eventType: NEWRELIC_TABLES.DEBUG_SWIGGY_ONE_JOURNEY,
                component: "M2C_HALFCARD",
                state: "MOUNTED",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldHideM2C]);

    const onM2CError = useCallback((error?: Error) => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.DEBUG_SWIGGY_ONE_JOURNEY,
            component: "M2C_HALFCARD",
            state: "ERROR_BOUNDARY",
            errorMessage: error?.message || "",
            errorName: error?.name || "",
        });
    }, []);

    if (shouldHideM2C) {
        return null;
    }

    return (
        <ErrorBoundary errorContent={null} errorFallback={onM2CError}>
            {isSwiggyBlackType ? (
                <M2CSwiggyBlackHalfCard {...m2cFormattedProps} />
            ) : (
                <M2CSwiggyOneHalfCard {...m2cFormattedProps} />
            )}
        </ErrorBoundary>
    );
};

export default React.memo(M2CHalfcard);
